/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";
import {
  RiskDetectionScope,
  RiskDetectionScope$Outbound,
  RiskDetectionScope$outboundSchema,
} from "./riskdetectionscope.js";
import {
  RiskMCPScope,
  RiskMCPScope$Outbound,
  RiskMCPScope$outboundSchema,
} from "./riskmcpscope.js";
import {
  RiskPolicyModelConfig,
  RiskPolicyModelConfig$Outbound,
  RiskPolicyModelConfig$outboundSchema,
} from "./riskpolicymodelconfig.js";

/**
 * Policy action: flag, warn (challenge), block, or quarantine (deny and freeze the hook session). MCP-scoped policies support flag and block only.
 */
export const UpdateRiskPolicyRequestBodyAction = {
  Flag: "flag",
  Warn: "warn",
  Block: "block",
  Quarantine: "quarantine",
} as const;
/**
 * Policy action: flag, warn (challenge), block, or quarantine (deny and freeze the hook session). MCP-scoped policies support flag and block only.
 */
export type UpdateRiskPolicyRequestBodyAction = ClosedEnum<
  typeof UpdateRiskPolicyRequestBodyAction
>;

/**
 * Policy audience type: everyone or targeted. Omit to preserve the current audience type.
 */
export const UpdateRiskPolicyRequestBodyAudienceType = {
  Everyone: "everyone",
  Targeted: "targeted",
} as const;
/**
 * Policy audience type: everyone or targeted. Omit to preserve the current audience type.
 */
export type UpdateRiskPolicyRequestBodyAudienceType = ClosedEnum<
  typeof UpdateRiskPolicyRequestBodyAudienceType
>;

/**
 * The policy's shadow MCP disposition. Immutable: omit, or send the current value unchanged; any other value is rejected. Switching posture requires delete + recreate.
 */
export const UpdateRiskPolicyRequestBodyShadowMcpDisposition = {
  BlockAll: "block_all",
  AllowAll: "allow_all",
} as const;
/**
 * The policy's shadow MCP disposition. Immutable: omit, or send the current value unchanged; any other value is rejected. Switching posture requires delete + recreate.
 */
export type UpdateRiskPolicyRequestBodyShadowMcpDisposition = ClosedEnum<
  typeof UpdateRiskPolicyRequestBodyShadowMcpDisposition
>;

export type UpdateRiskPolicyRequestBody = {
  /**
   * Policy action: flag, warn (challenge), block, or quarantine (deny and freeze the hook session). MCP-scoped policies support flag and block only.
   */
  action?: UpdateRiskPolicyRequestBodyAction | undefined;
  /**
   * For the account_identity source: corporate email domains considered approved. Omit to preserve the current list; send an empty array to clear it.
   */
  approvedEmailDomains?: Array<string> | undefined;
  /**
   * Principal URNs this policy applies to. Omit to preserve the current target principals.
   */
  audiencePrincipalUrns?: Array<string> | undefined;
  /**
   * Policy audience type: everyone or targeted. Omit to preserve the current audience type.
   */
  audienceType?: UpdateRiskPolicyRequestBodyAudienceType | undefined;
  /**
   * Whether the policy name should be auto-generated.
   */
  autoName?: boolean | undefined;
  /**
   * Custom detection rule ids to attach as detectors: a match produces a finding. Omit to preserve the current selection.
   */
  customRuleIds?: Array<string> | undefined;
  /**
   * Per-category detection scopes. Each specified category replaces its centrally recommended scope; a scope with both predicates empty scans every message surface. Omit to preserve the current value; send empty to clear.
   */
  detectionScopes?: Array<RiskDetectionScope> | undefined;
  /**
   * Canonical rule_ids the user has unchecked within otherwise-enabled categories. Matching findings are dropped at scan time.
   */
  disabledRules?: Array<string> | undefined;
  /**
   * Whether the policy is active.
   */
  enabled?: boolean | undefined;
  /**
   * The policy ID.
   */
  id: string;
  mcpScope?: RiskMCPScope | undefined;
  modelConfig?: RiskPolicyModelConfig | undefined;
  /**
   * The policy name.
   */
  name: string;
  /**
   * Presidio entity types to detect.
   */
  presidioEntities?: Array<string> | undefined;
  /**
   * Minimum Presidio confidence (0.0-1.0) a PII match must clear to surface. Omit/null applies the default (0.5).
   */
  presidioScoreThreshold?: number | undefined;
  /**
   * For prompt_based policies: the guardrail prompt the LLM judge evaluates each in-scope message against. Omit to preserve the current value.
   */
  prompt?: string | undefined;
  /**
   * Prompt-injection detection rule ids to enable in addition to the heuristic baseline.
   */
  promptInjectionRules?: Array<string> | undefined;
  /**
   * CVSS-style severity (0.1-10) assigned to findings this policy produces. Omit to preserve the current value.
   */
  score?: number | undefined;
  /**
   * Complete desired canonical URL allow set for this policy. Omit to preserve; send empty to clear.
   */
  shadowMcpAllowedUrls?: Array<string> | undefined;
  /**
   * For allow_all policies: complete desired canonical URL block set. Omit to preserve; send empty to clear.
   */
  shadowMcpBlockedUrls?: Array<string> | undefined;
  /**
   * The policy's shadow MCP disposition. Immutable: omit, or send the current value unchanged; any other value is rejected. Switching posture requires delete + recreate.
   */
  shadowMcpDisposition?:
    | UpdateRiskPolicyRequestBodyShadowMcpDisposition
    | undefined;
  /**
   * Detection sources to enable.
   */
  sources?: Array<string> | undefined;
  /**
   * Confirms that this edit may displace standing MCP approval decisions its URL lists contradict, transitioning them to superseded (audit-logged, decision history preserved). Without it, a contradicting edit is rejected with a conflict naming the affected servers.
   */
  supersedeDecisions?: boolean | undefined;
  /**
   * Optional message shown to end users when this policy blocks an action or surfaces a flagged finding. Send an empty string to clear.
   */
  userMessage?: string | undefined;
};

/** @internal */
export const UpdateRiskPolicyRequestBodyAction$outboundSchema: z.ZodMiniEnum<
  typeof UpdateRiskPolicyRequestBodyAction
> = z.enum(UpdateRiskPolicyRequestBodyAction);

/** @internal */
export const UpdateRiskPolicyRequestBodyAudienceType$outboundSchema:
  z.ZodMiniEnum<typeof UpdateRiskPolicyRequestBodyAudienceType> = z.enum(
    UpdateRiskPolicyRequestBodyAudienceType,
  );

/** @internal */
export const UpdateRiskPolicyRequestBodyShadowMcpDisposition$outboundSchema:
  z.ZodMiniEnum<typeof UpdateRiskPolicyRequestBodyShadowMcpDisposition> = z
    .enum(UpdateRiskPolicyRequestBodyShadowMcpDisposition);

/** @internal */
export type UpdateRiskPolicyRequestBody$Outbound = {
  action?: string | undefined;
  approved_email_domains?: Array<string> | undefined;
  audience_principal_urns?: Array<string> | undefined;
  audience_type?: string | undefined;
  auto_name?: boolean | undefined;
  custom_rule_ids?: Array<string> | undefined;
  detection_scopes?: Array<RiskDetectionScope$Outbound> | undefined;
  disabled_rules?: Array<string> | undefined;
  enabled?: boolean | undefined;
  id: string;
  mcp_scope?: RiskMCPScope$Outbound | undefined;
  model_config?: RiskPolicyModelConfig$Outbound | undefined;
  name: string;
  presidio_entities?: Array<string> | undefined;
  presidio_score_threshold?: number | undefined;
  prompt?: string | undefined;
  prompt_injection_rules?: Array<string> | undefined;
  score?: number | undefined;
  shadow_mcp_allowed_urls?: Array<string> | undefined;
  shadow_mcp_blocked_urls?: Array<string> | undefined;
  shadow_mcp_disposition?: string | undefined;
  sources?: Array<string> | undefined;
  supersede_decisions?: boolean | undefined;
  user_message?: string | undefined;
};

/** @internal */
export const UpdateRiskPolicyRequestBody$outboundSchema: z.ZodMiniType<
  UpdateRiskPolicyRequestBody$Outbound,
  UpdateRiskPolicyRequestBody
> = z.pipe(
  z.object({
    action: z.optional(UpdateRiskPolicyRequestBodyAction$outboundSchema),
    approvedEmailDomains: z.optional(z.array(z.string())),
    audiencePrincipalUrns: z.optional(z.array(z.string())),
    audienceType: z.optional(
      UpdateRiskPolicyRequestBodyAudienceType$outboundSchema,
    ),
    autoName: z.optional(z.boolean()),
    customRuleIds: z.optional(z.array(z.string())),
    detectionScopes: z.optional(z.array(RiskDetectionScope$outboundSchema)),
    disabledRules: z.optional(z.array(z.string())),
    enabled: z.optional(z.boolean()),
    id: z.string(),
    mcpScope: z.optional(RiskMCPScope$outboundSchema),
    modelConfig: z.optional(RiskPolicyModelConfig$outboundSchema),
    name: z.string(),
    presidioEntities: z.optional(z.array(z.string())),
    presidioScoreThreshold: z.optional(z.number()),
    prompt: z.optional(z.string()),
    promptInjectionRules: z.optional(z.array(z.string())),
    score: z.optional(z.number()),
    shadowMcpAllowedUrls: z.optional(z.array(z.string())),
    shadowMcpBlockedUrls: z.optional(z.array(z.string())),
    shadowMcpDisposition: z.optional(
      UpdateRiskPolicyRequestBodyShadowMcpDisposition$outboundSchema,
    ),
    sources: z.optional(z.array(z.string())),
    supersedeDecisions: z.optional(z.boolean()),
    userMessage: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      approvedEmailDomains: "approved_email_domains",
      audiencePrincipalUrns: "audience_principal_urns",
      audienceType: "audience_type",
      autoName: "auto_name",
      customRuleIds: "custom_rule_ids",
      detectionScopes: "detection_scopes",
      disabledRules: "disabled_rules",
      mcpScope: "mcp_scope",
      modelConfig: "model_config",
      presidioEntities: "presidio_entities",
      presidioScoreThreshold: "presidio_score_threshold",
      promptInjectionRules: "prompt_injection_rules",
      shadowMcpAllowedUrls: "shadow_mcp_allowed_urls",
      shadowMcpBlockedUrls: "shadow_mcp_blocked_urls",
      shadowMcpDisposition: "shadow_mcp_disposition",
      supersedeDecisions: "supersede_decisions",
      userMessage: "user_message",
    });
  }),
);

export function updateRiskPolicyRequestBodyToJSON(
  updateRiskPolicyRequestBody: UpdateRiskPolicyRequestBody,
): string {
  return JSON.stringify(
    UpdateRiskPolicyRequestBody$outboundSchema.parse(
      updateRiskPolicyRequestBody,
    ),
  );
}
